import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Server, BellRing } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Never');

const StatusBadge = ({ online, testId }) => {
  const [label, cls] = online === null || online === undefined ? ['Not checked yet', 'bg-gray-100 text-gray-600']
    : online ? ['Online', 'bg-emerald-50 text-emerald-700'] : ['Offline', 'bg-red-50 text-red-700'];
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${cls}`}>
      <span className={`w-2 h-2 rounded-full ${online ? 'bg-emerald-500 animate-pulse' : online === false ? 'bg-red-500' : 'bg-gray-400'}`} />{label}
    </span>
  );
};

const Row = ({ label, children, testId }) => (
  <div className="flex justify-between gap-4 text-sm py-2 border-b border-[#E6DAC5]/60 last:border-0">
    <span className="text-[#1E1622]/55">{label}</span><span className="text-[#1E1622] text-right" data-testid={testId}>{children}</span>
  </div>
);

const StudioCard = ({ s }) => (
  <div className="bg-white rounded-2xl border border-[#E6DAC5] p-6" data-testid={`studio-card-${s.studio}`}>
    <div className="flex items-start justify-between gap-3 mb-4">
      <div className="flex items-center gap-2"><Server size={18} className="text-[#3B164C]" /><h3 className="font-semibold text-[#1E1622]">{s.name}</h3></div>
      <StatusBadge online={s.online} testId={`studio-status-${s.studio}`} />
    </div>
    <Row label="Last successful sync" testId={`studio-last-sync-${s.studio}`}>{when(s.last_sync)}</Row>
    <Row label="Guides in last sync" testId={`studio-guide-count-${s.studio}`}>{s.guide_count ?? '—'}</Row>
    <Row label="Last checked" testId={`studio-last-checked-${s.studio}`}>{when(s.last_checked)}</Row>
    {s.online === false && s.offline_since && <Row label="Offline since" testId={`studio-offline-since-${s.studio}`}>{when(s.offline_since)}</Row>}
    {s.error && <p className="mt-3 text-xs text-red-700 bg-red-50 rounded-lg p-3 break-words" data-testid={`studio-error-${s.studio}`}>{s.error}</p>}
    {s.note && <p className="mt-3 text-xs text-[#8A6A2E] bg-[#C5A059]/10 rounded-lg p-3" data-testid={`studio-note-${s.studio}`}>{s.note}</p>}
  </div>
);

export const StudioStatusPanel = ({ authHeaders }) => {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/studio-status`, authHeaders).then((r) => setData(r.data)).catch(() => toast.error('Could not load studio status'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/admin/studio-status/sync`, {}, authHeaders);
      setData(r.data);
      const down = r.data.studios.filter((s) => s.online === false);
      const noted = r.data.studios.filter((s) => s.online && s.note);
      if (down.length) toast.error(`${down.map((s) => s.name).join(' and ')} could not be reached`);
      else if (noted.length) toast.warning(`Synced. ${noted.map((s) => `${s.name}: ${s.note}`).join('. ')}`);
      else toast.success('Both studios synced');
    } catch { toast.error('Sync failed. Please try again.'); } finally { setSyncing(false); }
  };

  return (
    <div className="space-y-6" data-testid="studio-status-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-xl font-semibold text-[#1E1622]">Studio Status</h2>
          <p className="text-sm text-[#1E1622]/60">The two apps that supply your guides. Checked automatically every 15 minutes.</p>
        </div>
        <button onClick={sync} disabled={syncing} data-testid="studio-sync-now-btn" className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-60">
          <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Syncing…' : 'Sync now'}
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-6">{(data?.studios || []).map((s) => <StudioCard key={s.studio} s={s} />)}</div>
      {data && (
        <p className="text-sm text-[#1E1622]/60 inline-flex items-center gap-2" data-testid="studio-alert-recipient">
          <BellRing size={14} className="text-[#C5A059]" /> If a studio stays offline for 10 minutes, an alert is emailed to {data.alert_to}.
        </p>
      )}
    </div>
  );
};
